import { memo, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Popover } from '@air/primitive-popover';
import { useBreakpointsContext } from '@air/provider-media-query';

import { MobileFiltersContainer } from '~/components/Filters/FiltersButton/MobileFiltersContainer';
import { FILTERS_DROPDOWN } from '~/constants/testIDs';
import { removeFiltersWithoutSelectionAction } from '~/store/filters/actions';

export type FiltersPopoverTriggerProps = {
  onClick: () => void;
  isActive: boolean;
};

export type FiltersDropdownTriggerProps = {
  renderTrigger: (params: FiltersPopoverTriggerProps) => ReactNode;
  renderFiltersDropdown: (closePopover: { closePopover: () => void }) => ReactNode;
};

export const FiltersDropdownTrigger = memo(({ renderFiltersDropdown, renderTrigger }: FiltersDropdownTriggerProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const dispatch = useDispatch();

  const toggleIsPopoverOpen = useCallback(async () => {
    setIsPopoverOpen((isOpen) => !isOpen);
  }, []);

  const popoverContent = useMemo(
    () => (
      <div className="flex flex-1 flex-col" data-testid={FILTERS_DROPDOWN}>
        {renderFiltersDropdown({ closePopover: toggleIsPopoverOpen })}
      </div>
    ),
    [renderFiltersDropdown, toggleIsPopoverOpen],
  );

  const triggerProps: FiltersPopoverTriggerProps = useMemo(
    () => ({
      isActive: isPopoverOpen,
      ref: triggerRef,
      onClick: toggleIsPopoverOpen,
    }),
    [isPopoverOpen, toggleIsPopoverOpen],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      /**
       * When the popover open state changes, we want to make sure to clear all
       * the filters that doesn't have a value associated with them. This also prevents
       * filters from persisting between route changes when this component is loaded in
       * the layout.
       */
      dispatch(removeFiltersWithoutSelectionAction());
    },
    [dispatch],
  );

  return (
    <>
      {!isAboveMediumScreen ? (
        <>
          {renderTrigger(triggerProps)}
          {isPopoverOpen && <MobileFiltersContainer onClose={toggleIsPopoverOpen} content={popoverContent} />}
        </>
      ) : (
        <Popover
          className="flex max-h-[calc(100vh-72px)] w-[100vw-48px] min-w-[400px] max-w-[400px] overflow-hidden p-0 md:max-h-[70vh]"
          onOpenChange={onOpenChange}
          open={isPopoverOpen}
          trigger={renderTrigger(triggerProps)}
        >
          {popoverContent}
        </Popover>
      )}
    </>
  );
});

FiltersDropdownTrigger.displayName = 'FiltersTrigger';
